import { Injectable } from "@angular/core";
import { databases, ID, storage } from "../config/appwrite";
import { environment } from "../../../environments/environment";
import { EspecialistaAgenda, SedeConsultorio } from "../models";
import { Query } from "appwrite";

@Injectable({
  providedIn: "root",
})
export class EspecialistaAgendaService {
  private collectionId = "especialistas_agenda";
  private collectionIdSedeConsultorio = "sedes_consultorios";
  private DATABASE_NAME = environment.databaseName;

  constructor() {}

  /**
   * @createDocument()
   * @param data
   * @returns
   */
  createDocument(data: EspecialistaAgenda) {
    return databases.createDocument(
      this.DATABASE_NAME,
      this.collectionId,
      ID.unique(),
      data
    );
  }

  /**
   *
   * @param idEspecialista
   * @returns
   */
  getDocumentsByEspecialista(
    idEspecialista: string
  ): Promise<EspecialistaAgenda[]> {
    let firstDate = new Date();
    firstDate.setHours(0, 0, 0, 0);
    let lastDate = new Date();
    lastDate.setMonth(lastDate.getMonth() + 12);
    lastDate.setHours(23, 59, 59, 999);
    return databases
      .listDocuments<EspecialistaAgenda>(
        this.DATABASE_NAME,
        this.collectionId,
        [
          Query.equal('idEspecialista', [idEspecialista]),
          Query.greaterThanEqual('fecha', firstDate.toISOString()),
          Query.lessThanEqual('fecha', lastDate.toISOString()),

          Query.limit(750),
        ]
      )
      .then((response) => response.documents);
  }

  /**
   *
   * @param idEspecialista
   * @param fecha
   * @returns
   */
  getDocumentsByEspecialistaAndDate(
    idEspecialista: string,
    idSedeConsultorio: string,
    fecha: string
  ): Promise<EspecialistaAgenda[]> {
    let firstDate = new Date(fecha);
    firstDate.setHours(0, 0, 0, 0);
    let endDate = new Date(fecha);
    endDate.setHours(23, 59, 59, 999);
    return databases
      .listDocuments<EspecialistaAgenda>(
        this.DATABASE_NAME,
        this.collectionId,
        [
          Query.equal("idEspecialista", idEspecialista),
          Query.equal("idSedeConsultorio", idSedeConsultorio),
          Query.greaterThanEqual("fecha", firstDate.toISOString()),
          Query.lessThanEqual("fecha", endDate.toISOString()),
        ]
      )
      .then((response) => response.documents);
  }

  /**
   *
   * @param idEspecialista
   * @param fecha
   * @returns
   */
  getDocumentsByEspecialistaAndDateWhitoutSede(
    idEspecialista: string,
    fecha: string
  ): Promise<EspecialistaAgenda[]> {
    let firstDate = new Date(fecha);
    firstDate.setHours(0, 0, 0, 0);
    let endDate = new Date(fecha);
    endDate.setHours(23, 59, 59, 999);
    return databases
      .listDocuments<EspecialistaAgenda>(
        this.DATABASE_NAME,
        this.collectionId,
        [
          Query.equal('idEspecialista', idEspecialista),
          Query.greaterThanEqual('fecha', firstDate.toISOString()),
          Query.lessThanEqual('fecha', endDate.toISOString()),
        ]
      )
      .then((response) => response.documents);
  }

  // Obtiene la agenda (especialistas_agenda) de un especialista, desde la fecha de hoy hasta siempre.
  // Se ajusta para traer la agenda del rango del calendario que el paciente esté viendo para que sea más rápido.
  // FUNCION QUE TRAE LOS ESPECIALISTAS Y SU AGENDA para reservar citas
  getDocumentsByEspecialistagreaterThanCurrentDate(
    idEspecialista: string
  ): Promise<EspecialistaAgenda[]> {
    let firstDate = new Date();
    firstDate.setHours(0, 0, 0, 0);

    let lastDate = new Date();
    lastDate.setMonth(lastDate.getMonth() + 6);
    lastDate.setHours(23, 59, 59, 999);

    return databases
      .listDocuments<EspecialistaAgenda>(
        this.DATABASE_NAME,
        this.collectionId,
        [
          Query.equal("idEspecialista", idEspecialista),
          Query.greaterThanEqual("fecha", firstDate.toISOString()),
          Query.lessThanEqual("fecha", lastDate.toISOString()),
          Query.orderAsc("fecha"),
          Query.limit(700),
        ]
      )
      .then((response) => response.documents);
  }

  async getDocumentsByEspecialistagreaterThanCurrentDate_Org(
    idEspecialista: string,
    dateRange: { start: Date; end: Date }
  ): Promise<EspecialistaAgenda[]> {
    // Extraemos start y end del objeto dateRange
    const { start, end } = dateRange;

    // Convertimos las fechas a ISO string
    const startISO = start.toISOString();
    const endISO = end.toISOString();

    const agendaResponse = await databases.listDocuments<EspecialistaAgenda>(
      this.DATABASE_NAME,
      this.collectionId, //especialistas_agenda
      [
        Query.equal("idEspecialista", idEspecialista),
        Query.greaterThanEqual("fecha", startISO),
        Query.lessThan("fecha", endISO),
        Query.select(["fecha", "hora", "$id", "idSedeConsultorio.$id"]),
      ]
    );

    const enrichedDocuments = await Promise.all(
      agendaResponse.documents.map(async (doc) => {
        const idSedeConsultorio = await this.getSedeConsultorio(
          doc.idSedeConsultorio["$id"]
        );

        return {
          ...doc,
          idSedeConsultorio,
        };
      })
    );

    return enrichedDocuments;
  }

  async getDocumentsByEspecialistagreaterThanCurrentDate_ORG(
    idEspecialista: string
  ): Promise<EspecialistaAgenda[]> {
    let firstDate = new Date();
    firstDate.setHours(0, 0, 0, 0);

    const agendaResponse = await databases.listDocuments<EspecialistaAgenda>(
      this.DATABASE_NAME,
      this.collectionId, //especialistas_agenda
      [
        Query.equal("idEspecialista", idEspecialista),
        Query.greaterThanEqual("fecha", firstDate.toISOString()),
        Query.select(["fecha", "hora", "$id", "idSedeConsultorio.$id"]),
      ]
    );

    const enrichedDocuments = await Promise.all(
      agendaResponse.documents.map(async (doc) => {
        const idSedeConsultorio = await this.getSedeConsultorio(
          doc.idSedeConsultorio["$id"]
        );

        return {
          ...doc,
          idSedeConsultorio,
        };
      })
    );

    return enrichedDocuments;
  }

  // TODO: Se debe traer el id de la sede
  private async getSedeConsultorio(
    idSedeConsultorio: string
  ): Promise<SedeConsultorio> {
    const response = await databases.getDocument<SedeConsultorio>(
      this.DATABASE_NAME,
      this.collectionIdSedeConsultorio,
      idSedeConsultorio
    );
    return response;
  }

  // ############################################################################

  // Obtiene la agenda de un solo especialista
  async getDocument(idDocument: string) {
    const agendaResponse = await databases.getDocument(
      this.DATABASE_NAME,
      this.collectionId,
      idDocument
    );
  
    const idSedeConsultorio = await this.getSedeConsultorio(
      agendaResponse.idSedeConsultorio.$id
    );
  
    const enrichedDocuments = {
      ...agendaResponse,
      idSedeConsultorio,
    };
  
    return enrichedDocuments;
  }

  /**
   *
   * @param idDocument
   * @param data
   * @returns
   */
  updateDocument(idDocument: string, data: EspecialistaAgenda) {
    data.$databaseId = "";
    data.$collectionId = "";
    data.$id = "";
    return databases.updateDocument(
      this.DATABASE_NAME,
      this.collectionId,
      idDocument,
      data
    );
  }
}
